import Divider from '@mui/material/Divider';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import LinkIcon from '@mui/icons-material/Link';
import Paper from '@mui/material/Paper';
import ToggleButton from '@mui/material/ToggleButton';
import { Editor } from '@tiptap/react';
import { styled } from '@mui/material';

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  '&': {
    padding: theme.spacing(0.5),
    border: 0,
    '&.Mui-disabled': {
      border: 0,
    },
    '&:not(:first-of-type)': {
      borderRadius: theme.shape.borderRadius,
    },
    '&:first-of-type': {
      borderRadius: theme.shape.borderRadius,
    },
  },
}));

type TiptapToolbarProps = {
  editor: Editor | null;
};

const TiptapToolbar = (props: TiptapToolbarProps) => {
  if (!props.editor) {
    return null;
  }
  return (
    <div>
      <Paper
        elevation={0}
        sx={{
          p: 1,
          display: 'flex',
          border: (theme) => `1px solid ${theme.palette.grey[400]}`,
          borderBottom: 0,
          borderRadius: (theme) =>
            `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <StyledToggleButton
          value="bold"
          selected={props.editor.isActive('bold')}
          onChange={() => {
            props.editor?.chain().focus().toggleBold().run();
          }}
        >
          <FormatBoldIcon />
        </StyledToggleButton>
        <StyledToggleButton
          value="italic"
          selected={props.editor.isActive('italic')}
          onChange={() => {
            props.editor?.chain().focus().toggleItalic().run();
          }}
        >
          <FormatItalicIcon />
        </StyledToggleButton>
        <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />
        <StyledToggleButton
          value="bulletList"
          selected={props.editor.isActive('bulletList')}
          onChange={() => {
            props.editor?.chain().focus().toggleBulletList().run();
          }}
        >
          <FormatListBulletedIcon />
        </StyledToggleButton>
        <StyledToggleButton
          value="orderedList"
          selected={props.editor.isActive('orderedList')}
          onChange={() => {
            props.editor?.chain().focus().toggleOrderedList().run();
          }}
        >
          <FormatListNumberedIcon />
        </StyledToggleButton>
        <StyledToggleButton
          value="blockquote"
          selected={props.editor.isActive('blockquote')}
          onChange={() => {
            props.editor?.chain().focus().toggleBlockquote().run();
          }}
        >
          <FormatQuoteIcon />
        </StyledToggleButton>

        <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />

        <StyledToggleButton
          value="link"
          selected={props.editor.isActive('link')}
          onChange={() => {
            const previousUrl = props.editor?.getAttributes('link').href;

            const url = window.prompt('URL', previousUrl);

            // cancelled
            if (url === null) {
              return;
            }

            // empty
            if (url === '') {
              props.editor
                ?.chain()
                .focus()
                .extendMarkRange('link')
                .unsetLink()
                .run();

              return;
            }

            // update link
            props.editor
              ?.chain()
              .focus()
              .extendMarkRange('link')
              .setLink({ href: url })
              .run();
          }}
        >
          <LinkIcon />
        </StyledToggleButton>
        <StyledToggleButton
          value="mui-button"
          selected={props.editor.isActive('mui-button')}
          onChange={() => {
            if (props.editor?.isActive('mui-button')) {
              props.editor?.chain().focus().unsetButton().run();
            } else {
              const previousUrl =
                props.editor?.getAttributes('mui-button').href;

              const url = window.prompt('URL', previousUrl);
              props.editor
                ?.chain()
                .focus()
                .setButton({ href: url || '' })
                .run();
            }
          }}
        >
          button
        </StyledToggleButton>
      </Paper>
    </div>
  );
};

export default TiptapToolbar;
